import { z } from "zod";
import { type Country } from "./countries";
import sharedBrandsContract from "./generated/brands-contract";

export const ALL_BRANDS = [
  "jeff-vn",
  "loanonline-ph",
  "finmart-id",
  "finmart-my",
  "finmart-vn",
  "dong247-com",
  "finmercado-mx",
  "mercadopeso-mx",
  "finmerkado-ph",
  "paisa247-in",
  "finmercado-es",
  "finmercado-co",
  "finmercado-pe",
  "finmercado-com-br",
] as const;

export const ALL_BRANDS_INCLUDING_SECONDARY = [
  ...ALL_BRANDS,
  "luminaonline-ph",
] as const;

export const BrandIdSchema = z.enum(ALL_BRANDS);

export type BrandId = z.infer<typeof BrandIdSchema>;

export const CLARITY_PROJECT_IDS = {
  "jeff-vn": "encgxx6ubt",
  "loanonline-ph": "endgm9ifrx",
  "finmart-id": null,
  "finmart-my": "yi6q9c7fz2",
  "finmart-vn": "jbxj9vierx",
  "dong247-com": null,
  "finmercado-mx": "ik69pvw1wt",
  "mercadopeso-mx": "qly22sfy4g",
  "finmerkado-ph": "jwzda7hdnp",
  "paisa247-in": "ocuspn0xkp",
  "finmercado-es": "ouutmf1cd7",
  "finmercado-co": "tc9d5xojpo",
  "finmercado-pe": "yi6ot6kjij",
  "finmercado-com-br": "yi6pjqezdd",
} as const satisfies Record<BrandId, string | null>;

const DomainSchema = z
  .string()
  .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/);

const HealthUrlSchema = z.url({ protocol: /^https$/ });

const BrandFeatureSchema = z.enum(["academy"]);

const GOOGLE_SIGN_IN_PLATFORMS = ["web", "mobile-app"] as const;

type GoogleSignInPlatform = (typeof GOOGLE_SIGN_IN_PLATFORMS)[number];

const GoogleSignInSchema = z.partialRecord(
  z.enum(GOOGLE_SIGN_IN_PLATFORMS),
  z.string().trim().min(1),
);

const SharedBrandsSchema = z
  .object({
    active: z.array(
      z
        .object({
          id: z.string().trim().min(1),
          canonicalDomain: DomainSchema,
          timezone: z.string().optional(),
          features: z.array(BrandFeatureSchema).optional(),
          googleSignIn: GoogleSignInSchema.optional(),
          locales: z.array(z.string()).optional(),
          mainBrandId: z.string().trim().min(1).optional(),
          ownedDomains: z.array(DomainSchema),
          health: z.record(DomainSchema, HealthUrlSchema.nullable()),
          inactiveDomains: z.array(DomainSchema).optional(),
        })
        .strict()
        .refine(
          (brand) =>
            [...Object.keys(brand.health)].sort().join() ===
            [...brand.ownedDomains].sort().join(),
          { message: "health keys must match ownedDomains", path: ["health"] },
        ),
    ),
    inactive: z.array(
      z
        .object({
          id: z.string().trim().min(1),
          canonicalDomain: DomainSchema,
          timezone: z.string().optional(),
          features: z.array(BrandFeatureSchema).optional(),
          locales: z.array(z.string()).optional(),
          mainBrandId: z.string().trim().min(1).optional(),
          ownedDomains: z.array(DomainSchema),
          inactiveDomains: z.array(DomainSchema).optional(),
        })
        .strict(),
    ),
  })
  .strict();

const sharedBrands = SharedBrandsSchema.parse(sharedBrandsContract);
const SHARED_BRANDS = [...sharedBrands.active, ...sharedBrands.inactive];
const SHARED_BRANDS_BY_ID = new Map(
  SHARED_BRANDS.map((brand) => [brand.id, brand]),
);

const ACTIVE_SHARED_BRANDS_BY_ID = new Map(
  sharedBrands.active.map((brand) => [brand.id, brand]),
);

if (SHARED_BRANDS_BY_ID.size !== SHARED_BRANDS.length) {
  throw new Error("shared/brands.json contains duplicate brand IDs");
}

const OWNED_DOMAINS = SHARED_BRANDS.flatMap((brand) => brand.ownedDomains).sort(
  (left, right) => right.length - left.length,
);
const INACTIVE_DOMAINS = SHARED_BRANDS.flatMap(
  (brand) => brand.inactiveDomains ?? [],
);
const BRAND_IDS_BY_ROOT_DOMAIN = new Map(
  ALL_BRANDS.map((brandId) => {
    const brand = SHARED_BRANDS_BY_ID.get(brandId);
    if (!brand) {
      throw new Error(`brandId=${brandId} not found in shared/brands.json`);
    }
    return [brand.canonicalDomain, brandId] as const;
  }),
);

if (new Set(OWNED_DOMAINS).size !== OWNED_DOMAINS.length) {
  throw new Error("shared/brands.json contains duplicate owned domains");
}
if (new Set(INACTIVE_DOMAINS).size !== INACTIVE_DOMAINS.length) {
  throw new Error("shared/brands.json contains duplicate inactive domains");
}
if (INACTIVE_DOMAINS.some((domain) => OWNED_DOMAINS.includes(domain))) {
  throw new Error("shared/brands.json contains active and inactive domains");
}
if (BRAND_IDS_BY_ROOT_DOMAIN.size !== ALL_BRANDS.length) {
  throw new Error("shared/brands.json contains duplicate canonical domains");
}

const getCanonicalDomain = (brandId: string): string => {
  const brand = SHARED_BRANDS_BY_ID.get(brandId);
  if (!brand) {
    throw new Error(`brandId=${brandId} not found in shared/brands.json`);
  }
  return brand.canonicalDomain;
};

export const getBrandFeatures = (brandId: BrandId) =>
  SHARED_BRANDS_BY_ID.get(brandId)?.features ?? [];

export const getGoogleSignInAccount = (
  brandId: string,
  platform: GoogleSignInPlatform,
): string | undefined =>
  ACTIVE_SHARED_BRANDS_BY_ID.get(brandId)?.googleSignIn?.[platform];

export const getBrandLocales = (brandId: BrandId): string[] =>
  SHARED_BRANDS_BY_ID.get(brandId)?.locales ?? [];

export const getBrandTimezone = (brandId: BrandId): string => {
  const timezone = SHARED_BRANDS_BY_ID.get(brandId)?.timezone;
  if (!timezone) throw new Error(`No timezone configured for ${brandId}`);
  return timezone;
};

export const getOwnedBrandDomain = (hostname: string): string | null => {
  const normalized = hostname.trim().toLowerCase().replace(/\.+$/, "");
  return (
    OWNED_DOMAINS.find(
      (domain) => normalized === domain || normalized.endsWith(`.${domain}`),
    ) ?? null
  );
};

export const getRegistrableDomain = (
  origin: string | undefined | null,
): string | undefined => {
  if (!origin) {
    return undefined;
  }

  const hostname = origin
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
    .split(/[/?#:]/)[0];

  if (!hostname) {
    return undefined;
  }

  // Registry lookup, not the last two labels: finmercado.com.br truncates to the
  // public suffix com.br, which browsers silently refuse to store cookies on.
  return (
    getOwnedBrandDomain(hostname) ?? hostname.split(".").slice(-2).join(".")
  );
};

const ALL_BRAND_NAMES = [
  "Jeff",
  "LoanOnline",
  "Finmart",
  "Finmercado",
  "MercadoPeso",
  "Finmerkado",
  "Paisa247",
  "Dong247",
] as const;

export const BrandNameSchema = z.enum(ALL_BRAND_NAMES);

export type BrandName = z.infer<typeof BrandNameSchema>;

type BrandConfig = {
  country: Country;
  hostname: string;
  logoUrl: string;
  brandName: BrandName;
  brandType: BrandType;
};

type BrandType = "payday" | "new-verticals";

const CONFIG_ENTRIES: Record<BrandId, BrandConfig> = {
  "jeff-vn": {
    country: "VN",
    hostname: getCanonicalDomain("jeff-vn"),
    brandName: "Jeff",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/6107d6546b656bffc909cd26/61ca373978cdb708b22fc596_logo-blue.svg",
  },
  "loanonline-ph": {
    country: "PH",
    hostname: getCanonicalDomain("loanonline-ph"),
    brandName: "LoanOnline",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/60f982c176db242a90ba53bd/60ffde2e2decb2378ecb9160_lo.svg",
  },
  "finmart-id": {
    country: "ID",
    hostname: getCanonicalDomain("finmart-id"),
    brandName: "Finmart",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/63a36117c5d5e4318ab6e069/6570293216cbe2631a18efed_finmart-vn-logo-vertical-dark.svg",
  },
  "finmart-my": {
    country: "MY",
    hostname: getCanonicalDomain("finmart-my"),
    brandName: "Finmart",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/63a36117c5d5e4318ab6e069/6570293216cbe2631a18efed_finmart-vn-logo-vertical-dark.svg",
  },
  "finmart-vn": {
    country: "VN",
    hostname: getCanonicalDomain("finmart-vn"),
    brandName: "Finmart",
    brandType: "new-verticals",
    logoUrl:
      "https://cdn.prod.website-files.com/64451f6c103dd2b230a02f66/64452073b7dd70b829118a9d_logo-finmart-n.svg",
  },
  "dong247-com": {
    country: "VN",
    hostname: getCanonicalDomain("dong247-com"),
    brandName: "Dong247",
    brandType: "payday",
    logoUrl: "https://www.dong247.com/images/dong-logo.png",
  },
  "finmercado-mx": {
    country: "MX",
    hostname: getCanonicalDomain("finmercado-mx"),
    brandName: "Finmercado",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/64eb88ea414665ada600ef81/64f4f7b475a4071825e59c3b_logo-main-b.svg",
  },
  "mercadopeso-mx": {
    country: "MX",
    hostname: getCanonicalDomain("mercadopeso-mx"),
    brandName: "MercadoPeso",
    brandType: "new-verticals",
    logoUrl:
      "https://cdn.mercadopeso.mx/assets/brands/mercadopeso-color-multi-horizontal-medium.svg",
  },
  "finmerkado-ph": {
    country: "PH",
    hostname: getCanonicalDomain("finmerkado-ph"),
    brandName: "Finmerkado",
    brandType: "new-verticals",
    logoUrl:
      "https://cdn.prod.website-files.com/64eb88ea414665ada600ef81/64f4f7b475a4071825e59c3b_logo-main-b.svg",
  },
  "paisa247-in": {
    country: "IN",
    hostname: getCanonicalDomain("paisa247-in"),
    brandName: "Paisa247",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/626954ed2266302bc00589f5/63b5d81f53ecb170c28cd97d_logo-n.svg",
  },
  "finmercado-es": {
    country: "ES",
    hostname: getCanonicalDomain("finmercado-es"),
    brandName: "Finmercado",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/64b7dffde58b2870cfdd99d5/64be96bc6128a130a7e75d08_64452073b7dd70b829118a9d_logo-finmart-n.svg",
  },
  "finmercado-co": {
    country: "CO",
    hostname: getCanonicalDomain("finmercado-co"),
    brandName: "Finmercado",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/64b7dffde58b2870cfdd99d5/64be96bc6128a130a7e75d08_64452073b7dd70b829118a9d_logo-finmart-n.svg",
  },
  "finmercado-pe": {
    country: "PE",
    hostname: getCanonicalDomain("finmercado-pe"),
    brandName: "Finmercado",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/64b7dffde58b2870cfdd99d5/64be96bc6128a130a7e75d08_64452073b7dd70b829118a9d_logo-finmart-n.svg",
  },
  "finmercado-com-br": {
    country: "BR",
    hostname: getCanonicalDomain("finmercado-com-br"),
    brandName: "Finmercado",
    brandType: "payday",
    logoUrl:
      "https://cdn.prod.website-files.com/64b7dffde58b2870cfdd99d5/64be96bc6128a130a7e75d08_64452073b7dd70b829118a9d_logo-finmart-n.svg",
  },
};

export const getBrandConfig = (brandId: BrandId): BrandConfig => {
  const config = CONFIG_ENTRIES[brandId];
  if (!config) {
    throw new Error(`brandId=${brandId} not found in config`);
  }
  return config;
};

export const getBrandIdForRootDomain = (rootDomain: string): BrandId => {
  const brandId = BRAND_IDS_BY_ROOT_DOMAIN.get(rootDomain);
  if (brandId) {
    return brandId;
  }

  throw new Error(`No brand id mapped for root domain: ${rootDomain}`);
};

export const getCountryCode = (brandId: BrandId): Country => {
  return CONFIG_ENTRIES[brandId].country;
};
